import { usersAPI } from '@/api'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import type {
  CrmIntegrationOutgoingAttachment,
  CrmIntegrationOutgoingAttachmentKind,
} from '../types/crm-integration-chat.types'

/** @deprecated Используйте getCrmIntegrationOutgoingFileAccept(provider). */
export const CRM_INTEGRATION_OUTGOING_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
] as const

/** @deprecated */
export const CRM_INTEGRATION_OUTGOING_IMAGE_ACCEPT =
  CRM_INTEGRATION_OUTGOING_IMAGE_EXTENSIONS.map((ext) => `.${ext}`).join(',')

/** @deprecated */
export const CRM_INTEGRATION_OUTGOING_DOCUMENT_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'rtf',
  'csv',
  'zip',
  'rar',
  '7z',
  'tar',
  'gz',
] as const

/** @deprecated */
export const CRM_INTEGRATION_OUTGOING_VIDEO_EXTENSIONS = [
  'mp4',
  'mov',
  'mkv',
  'webm',
  'avi',
] as const

/** @deprecated Используйте getCrmIntegrationOutgoingFileAccept(provider). */
export const CRM_INTEGRATION_OUTGOING_FILE_ACCEPT = [
  ...CRM_INTEGRATION_OUTGOING_IMAGE_EXTENSIONS,
  ...CRM_INTEGRATION_OUTGOING_DOCUMENT_EXTENSIONS,
  ...CRM_INTEGRATION_OUTGOING_VIDEO_EXTENSIONS,
]
  .map((ext) => `.${ext}`)
  .join(',')

const MB = 1024 * 1024

const TELEGRAM_IMAGE_MAX_BYTES = 10 * MB
const TELEGRAM_FILE_MAX_BYTES = 50 * MB
const VK_IMAGE_MAX_BYTES = 50 * MB
const VK_VOICE_MAX_BYTES = 25 * MB
const VK_DOC_MAX_BYTES = 200 * MB
const TELEGRAM_VIDEO_MAX_BYTES = 50 * MB

/** Wazzup: любое вложение ≤ 10 МБ (эффективный CRM-потолок). */
export const WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES = 10 * MB

const TELEGRAM_PHOTO_EXTS = new Set(['jpg', 'jpeg', 'png'])
const TELEGRAM_ANIMATION_EXTS = new Set(['gif'])
const TELEGRAM_AUDIO_EXTS = new Set(['mp3', 'm4a'])
const TELEGRAM_HINT_ACCEPT = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'mp4',
  'mp3',
  'm4a',
  'ogg',
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'zip',
] as const

const VK_IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp'])
const VK_VOICE_EXTS = new Set(['ogg', 'webm', 'm4a', 'mp4', 'wav'])
const VK_DOC_EXTS = new Set([
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'rtf',
  'csv',
  'zip',
  'rar',
  '7z',
  'tar',
  'gz',
  'mp3',
  'wav',
  'aac',
  'm4a',
  'mp4',
  'mov',
  'avi',
  'webm',
])

const VK_FILE_ACCEPT = Array.from(
  new Set([...VK_IMAGE_EXTS, ...VK_VOICE_EXTS, ...VK_DOC_EXTS]),
)
  .map((ext) => `.${ext}`)
  .join(',')

const IMAGE_EXTENSION_SET = new Set<string>(
  CRM_INTEGRATION_OUTGOING_IMAGE_EXTENSIONS,
)

const DOCUMENT_EXTENSION_SET = new Set<string>(
  CRM_INTEGRATION_OUTGOING_DOCUMENT_EXTENSIONS,
)

const VIDEO_EXTENSION_SET = new Set<string>(
  CRM_INTEGRATION_OUTGOING_VIDEO_EXTENSIONS,
)

export type CrmIntegrationUploadedUserFile = {
  id: number
  path: string | null
  name: string | null
}

const getUploadErrorText = (fileName: string): string =>
  `Не удалось загрузить файл «${fileName}»`

const getFileExtension = (fileName: string): string => {
  const match = fileName.trim().match(/\.([^.]+)$/i)
  return match ? match[1].toLowerCase() : ''
}

const formatOutgoingLimitBytes = (sizeBytes: number): string => {
  if (sizeBytes < MB) {
    return `${Math.round(sizeBytes / 1024)} КБ`
  }
  return `${Math.round(sizeBytes / MB)} МБ`
}

export const isCrmIntegrationOutgoingImageFile = (file: File): boolean => {
  if (file.type.startsWith('image/')) {
    return true
  }
  return IMAGE_EXTENSION_SET.has(getFileExtension(file.name))
}

export const isCrmIntegrationOutgoingDocumentFile = (file: File): boolean => {
  if (isCrmIntegrationOutgoingVideoFile(file)) {
    return false
  }
  if (
    file.type.startsWith('application/') ||
    file.type === 'text/plain' ||
    file.type === 'text/csv' ||
    file.type === 'text/rtf'
  ) {
    return !file.type.startsWith('image/')
  }
  return DOCUMENT_EXTENSION_SET.has(getFileExtension(file.name))
}

export const isCrmIntegrationOutgoingVideoFile = (file: File): boolean => {
  if (file.type.startsWith('video/')) {
    return true
  }
  return VIDEO_EXTENSION_SET.has(getFileExtension(file.name))
}

export const isCrmIntegrationOutgoingAttachableFile = (file: File): boolean =>
  isCrmIntegrationOutgoingImageFile(file) ||
  isCrmIntegrationOutgoingDocumentFile(file) ||
  isCrmIntegrationOutgoingVideoFile(file)

export const getCrmIntegrationOutgoingFileAccept = (
  provider: CrmChatProviderKey,
): string => {
  if (provider === 'telegram') {
    return TELEGRAM_HINT_ACCEPT.map((ext) => `.${ext}`).join(',')
  }
  if (provider === 'vkontakte') {
    return VK_FILE_ACCEPT
  }
  // MAX: без жёсткого whitelist — пустой accept = любой файл.
  if (provider === 'max') {
    return ''
  }
  return CRM_INTEGRATION_OUTGOING_FILE_ACCEPT
}

export const getCrmIntegrationOutgoingAttachmentsHint = (
  provider: CrmChatProviderKey,
): string => {
  switch (provider) {
    case 'telegram':
      return 'Изображение: JPG, PNG. Документ: PDF, DOC, XLS, ZIP и др. Видео: MP4. GIF — как анимация. Текст необязателен, если есть файл.'
    case 'vkontakte':
      return 'Фото: JPG, PNG, GIF, WEBP (до 50 МБ). Документы/видео/аудио: PDF, Office, ZIP, MP3, MP4, MOV и др. (до 200 МБ). Голосовое: OGG, WEBM, M4A, MP4, WAV (до 25 МБ). До 10 вложений.'
    case 'max':
      return 'Можно изображение, файл, видео, аудио или голосовое (до 10 вложений). Текст необязателен, если есть файл.'
    default:
      return 'Можно прикрепить файл. Текст необязателен, если есть вложение.'
  }
}

export const isCrmIntegrationOutgoingVoiceRecorderAllowed = (
  provider: CrmChatProviderKey,
): boolean =>
  provider === 'telegram' ||
  provider === 'vkontakte' ||
  provider === 'max'

type InferKindOptions = {
  forceVoice?: boolean
  provider?: CrmChatProviderKey
}

export const inferCrmIntegrationOutgoingAttachmentKind = (
  file: File,
  options?: InferKindOptions,
): CrmIntegrationOutgoingAttachmentKind => {
  if (options?.forceVoice) {
    return 'voice'
  }

  const provider = options?.provider
  const ext = getFileExtension(file.name)
  const mime = (file.type || '').toLowerCase()

  if (provider === 'telegram') {
    if (TELEGRAM_ANIMATION_EXTS.has(ext) || mime === 'image/gif') {
      return 'animation'
    }
    if (TELEGRAM_PHOTO_EXTS.has(ext) || mime === 'image/jpeg' || mime === 'image/png') {
      return 'image'
    }
    // WEBP и прочие «картинки» — как file (sendDocument), не image.
    if (ext === 'webp' || mime === 'image/webp') {
      return 'file'
    }
    if (
      ext === 'mp4' ||
      mime === 'video/mp4' ||
      mime.startsWith('video/')
    ) {
      return 'video'
    }
    if (TELEGRAM_AUDIO_EXTS.has(ext) || mime.startsWith('audio/')) {
      return 'audio'
    }
    return 'file'
  }

  if (provider === 'vkontakte') {
    if (VK_IMAGE_EXTS.has(ext) || mime.startsWith('image/')) {
      return 'image'
    }
    if (mime.startsWith('video/') || VIDEO_EXTENSION_SET.has(ext)) {
      return 'video'
    }
    if (mime.startsWith('audio/') || ['mp3', 'wav', 'aac', 'm4a'].includes(ext)) {
      return 'audio'
    }
    return 'file'
  }

  // MAX и fallback: мягкая классификация без жёсткого whitelist.
  if (ext === 'gif' || mime === 'image/gif') {
    return 'animation'
  }
  if (isCrmIntegrationOutgoingImageFile(file)) {
    return 'image'
  }
  if (isCrmIntegrationOutgoingVideoFile(file) || mime.startsWith('video/')) {
    return 'video'
  }
  if (mime.startsWith('audio/')) {
    return 'audio'
  }
  return 'file'
}

/** Валидация исходящего файла для TG / VK / MAX (не Wazzup). */
export const validateCrmIntegrationOutgoingAttachment = (
  file: File,
  provider: CrmChatProviderKey,
): string | null => {
  if (provider === 'wazzup') {
    return null
  }

  const ext = getFileExtension(file.name)
  const kind = inferCrmIntegrationOutgoingAttachmentKind(file, { provider })

  if (provider === 'telegram') {
    if (kind === 'image') {
      if (!TELEGRAM_PHOTO_EXTS.has(ext) && file.type !== 'image/jpeg' && file.type !== 'image/png') {
        return 'Для Telegram изображение должно быть JPG или PNG. GIF отправьте как файл — уйдёт анимацией; WEBP — как документ.'
      }
      if (file.size > TELEGRAM_IMAGE_MAX_BYTES) {
        return `Размер изображения не должен превышать ${formatOutgoingLimitBytes(
          TELEGRAM_IMAGE_MAX_BYTES,
        )}`
      }
      return null
    }
    if (kind === 'animation' && file.size > TELEGRAM_FILE_MAX_BYTES) {
      return `Размер анимации не должен превышать ${formatOutgoingLimitBytes(
        TELEGRAM_FILE_MAX_BYTES,
      )}`
    }
    if (kind === 'video') {
      const isMp4 =
        file.type === 'video/mp4' || ext === 'mp4' || file.type.includes('mp4')
      if (ext && !isMp4) {
        return 'Для Telegram отправляйте видео в формате MP4'
      }
      if (file.size > TELEGRAM_VIDEO_MAX_BYTES) {
        return `Размер видео не должен превышать ${formatOutgoingLimitBytes(
          TELEGRAM_VIDEO_MAX_BYTES,
        )}`
      }
      return null
    }
    if (file.size > TELEGRAM_FILE_MAX_BYTES) {
      return `Размер файла не должен превышать ${formatOutgoingLimitBytes(
        TELEGRAM_FILE_MAX_BYTES,
      )}`
    }
    return null
  }

  if (provider === 'vkontakte') {
    if (kind === 'image') {
      if (
        ext &&
        !VK_IMAGE_EXTS.has(ext) &&
        !file.type.startsWith('image/')
      ) {
        return 'Для VK фото: JPG, PNG, GIF, WEBP'
      }
      if (file.size > VK_IMAGE_MAX_BYTES) {
        return `Размер фото не должен превышать ${formatOutgoingLimitBytes(
          VK_IMAGE_MAX_BYTES,
        )}`
      }
      return null
    }
    if (kind === 'voice') {
      if (file.size > VK_VOICE_MAX_BYTES) {
        return `Размер голосового не должен превышать ${formatOutgoingLimitBytes(
          VK_VOICE_MAX_BYTES,
        )}`
      }
      return null
    }
    if (file.size > VK_DOC_MAX_BYTES) {
      return `Размер файла не должен превышать ${formatOutgoingLimitBytes(
        VK_DOC_MAX_BYTES,
      )}`
    }
    return null
  }

  if (provider === 'max') {
    // Без жёсткого whitelist — ошибки покажет API.
    return null
  }

  return null
}

/** @deprecated Используйте validateCrmIntegrationOutgoingAttachment. */
export const validateCrmIntegrationOutgoingVideoFile = (
  file: File,
  provider: CrmChatProviderKey,
): string | null => {
  if (!isCrmIntegrationOutgoingVideoFile(file)) {
    return null
  }
  return validateCrmIntegrationOutgoingAttachment(file, provider)
}

/** @deprecated Для Wazzup используйте validateWazzupOutgoingAttachment. */
export const validateCrmIntegrationOutgoingAttachmentSize = (
  file: File,
  provider: CrmChatProviderKey,
): string | null => {
  if (provider !== 'wazzup') {
    return null
  }
  if (file.size > WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES) {
    return `Размер вложения для Wazzup не должен превышать ${formatOutgoingLimitBytes(
      WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    )}`
  }
  return null
}

const parseUploadedUserFilePayload = (res: unknown): Record<string, unknown> | null => {
  if (!res || typeof res !== 'object') {
    return null
  }

  const record = res as { status?: unknown; data?: unknown }
  if (record.status === 'success' && record.data != null && typeof record.data === 'object') {
    return record.data as Record<string, unknown>
  }

  const axiosLike = res as { data?: { status?: unknown; data?: unknown } }
  if (
    axiosLike.data?.status === 'success' &&
    axiosLike.data.data != null &&
    typeof axiosLike.data.data === 'object'
  ) {
    return axiosLike.data.data as Record<string, unknown>
  }

  return null
}

const parseUserFileDetailPayload = (res: unknown): Record<string, unknown> | null => {
  const direct = parseUploadedUserFilePayload(res)
  if (direct != null) {
    return direct
  }

  if (!res || typeof res !== 'object') {
    return null
  }

  const axiosLike = res as { data?: unknown }
  if (axiosLike.data != null && typeof axiosLike.data === 'object') {
    return axiosLike.data as Record<string, unknown>
  }

  return null
}

const getMaxOutgoingPublicOrigin = (): string | null => {
  const origin = process.env.REACT_APP_OUTGOING_ATTACHMENT_PUBLIC_ORIGIN?.trim()
  return origin ? origin.replace(/\/$/, '') : null
}

const replaceLocalhostOrigin = (url: string): string => {
  const publicOrigin = getMaxOutgoingPublicOrigin()
  if (!publicOrigin) {
    return url
  }

  try {
    const parsed = new URL(url)
    if (parsed.hostname !== 'localhost' && parsed.hostname !== '127.0.0.1') {
      return url
    }

    return `${publicOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return url
  }
}

/**
 * Публичный HTTPS URL для MAX send — MAX скачивает файл снаружи.
 * Локалка: REACT_APP_OUTGOING_ATTACHMENT_PUBLIC_ORIGIN = ngrok на API :8000.
 */
const resolveMaxOutgoingPublicUrl = (path: string | null | undefined): string | null => {
  if (!path?.trim()) {
    return null
  }

  const trimmed = path.trim()
  const publicOrigin = getMaxOutgoingPublicOrigin()

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return replaceLocalhostOrigin(trimmed)
  }

  if (!publicOrigin) {
    return null
  }

  const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return `${publicOrigin}${normalizedPath}`
}

const resolveUserFileContentType = (
  file: File,
  options?: { forceVoice?: boolean; provider?: CrmChatProviderKey },
): string | null => {
  const kind = inferCrmIntegrationOutgoingAttachmentKind(file, options)

  if (kind === 'image') {
    return 'image'
  }

  if (kind === 'file' || kind === 'animation') {
    return 'document'
  }

  return null
}

export const uploadCrmIntegrationUserFile = async (
  file: File,
  userId: number,
  options?: {
    displayName?: string
    forceVoice?: boolean
    provider?: CrmChatProviderKey
  },
): Promise<CrmIntegrationUploadedUserFile> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('UserFilesForm[user_id]', String(userId))
  formData.append('UserFilesForm[name]', options?.displayName ?? file.name)

  const contentType = resolveUserFileContentType(file, options)
  if (contentType) {
    formData.append('content_type', contentType)
  }

  const res = await usersAPI.uploadPlatformMedia(formData)

  const data = parseUploadedUserFilePayload(res)
  if (!data) {
    throw new Error(getUploadErrorText(file.name))
  }

  const fileId = Number(data.id)

  if (!Number.isFinite(fileId) || fileId <= 0) {
    throw new Error(getUploadErrorText(file.name))
  }

  let path = typeof data?.path === 'string' ? data.path : null

  if (!path) {
    const detailRes = await usersAPI.getUserFile(fileId)
    const detail = parseUserFileDetailPayload(detailRes)
    path = typeof detail?.path === 'string' ? detail.path : null
  }

  return {
    id: fileId,
    path,
    name: typeof data.name === 'string' ? data.name : file.name,
  }
}

export const buildCrmIntegrationOutgoingAttachment = (
  file: File,
  uploaded: CrmIntegrationUploadedUserFile,
  provider: CrmChatProviderKey,
  options?: { forceVoice?: boolean; duration?: number | null },
): CrmIntegrationOutgoingAttachment => {
  const kind = inferCrmIntegrationOutgoingAttachmentKind(file, {
    ...options,
    provider,
  })
  const attachment: CrmIntegrationOutgoingAttachment = {
    kind,
    name: file.name,
    mime: file.type || undefined,
    size: file.size,
  }

  if (
    kind === 'voice' &&
    options?.duration != null &&
    Number.isFinite(options.duration) &&
    options.duration > 0
  ) {
    attachment.duration = Math.round(options.duration)
  }

  if (provider === 'max') {
    const url = resolveMaxOutgoingPublicUrl(uploaded.path)
    if (!url) {
      throw new Error(
        'Для отправки вложений в MAX задайте REACT_APP_OUTGOING_ATTACHMENT_PUBLIC_ORIGIN (ngrok на API :8000)',
      )
    }
    return {
      ...attachment,
      url,
    }
  }

  // Wazzup предпочитает публичный url (+ size/name уже в attachment).
  if (provider === 'wazzup') {
    const url = resolveMaxOutgoingPublicUrl(uploaded.path)
    return {
      ...attachment,
      user_file_id: uploaded.id,
      ...(url ? { url } : {}),
    }
  }

  return {
    ...attachment,
    user_file_id: uploaded.id,
  }
}

export const uploadCrmIntegrationOutgoingAttachment = async (
  file: File,
  userId: number,
  provider: CrmChatProviderKey,
  options?: {
    displayName?: string
    forceVoice?: boolean
    duration?: number | null
  },
): Promise<CrmIntegrationOutgoingAttachment> => {
  const uploaded = await uploadCrmIntegrationUserFile(file, userId, {
    ...options,
    provider,
  })
  return buildCrmIntegrationOutgoingAttachment(file, uploaded, provider, options)
}
